/**
 * SEO Schema Generators — JSON-LD structured data for rich search results.
 * Centralized schema generation with proper typing.
 */

import { siteConfig } from "@/config/site";

/**
 * Schema.org image/logo/url fields must be absolute -- Google treats a site-relative
 * path as invalid and drops the field, which silently costs the rich result. Product and
 * article images are stored relative ("/plantpicture/p1/5.webp"), so they are resolved
 * against the canonical origin here rather than at each call site.
 */
function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${siteConfig.url}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}
import { type Product, getProductSlug } from "@/lib/products";

/**
 * Serialises a JSON-LD payload for `dangerouslySetInnerHTML`.
 *
 * `JSON.stringify` alone is not safe inside a `<script>` element: a product name,
 * category title or FAQ answer containing `</script>` closes the tag early and everything
 * after it becomes live markup for every visitor to that page. Several of these fields
 * are admin-editable, so the escaping has to hold for arbitrary text. `<` is escaped
 * (which also neutralises `</script` and `<!--`), along with the two line-separator
 * code points JSON.stringify leaves raw even though JS string literals reject them.
 */
export function safeJsonLd(schema: unknown): string {
  return JSON.stringify(schema)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteConfig.name,
    alternateName: [...siteConfig.brand.alternateNames],
    url: siteConfig.url,
    logo: absoluteUrl(siteConfig.ogImage),
    description: siteConfig.description,
    telephone: siteConfig.contact.phone,
    address: {
      "@type": "PostalAddress",
      addressRegion: "Tamil Nadu",
      addressCountry: "IN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: siteConfig.contact.phone,
      contactType: "customer service",
      email: siteConfig.contact.email,
      availableLanguage: ["English", "Tamil", "Hindi"],
    },
    sameAs: Object.values(siteConfig.social),
  };
}

export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    // The brand, not the homepage <title>. These were the same string until defaultTitle
    // became keyword-led; carrying that into WebSite.name would tell Google the site is
    // *called* "Artificial Flowers & Plants for Home Decoration", which is the sitelinks and
    // knowledge-panel label. Those want the name a person would say out loud.
    name: siteConfig.name,
    alternateName: [...siteConfig.brand.alternateNames],
    url: siteConfig.url,
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteConfig.url}/shop?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function getContactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: `Contact Us — ${siteConfig.name}`,
    description: `Contact Aster Decorz for botanical orders, artificial plants, or customer support.`,
    url: `${siteConfig.url}/contact`,
    mainEntity: {
      "@type": "LocalBusiness",
      name: siteConfig.name,
      alternateName: [...siteConfig.brand.alternateNames],
      image: absoluteUrl(siteConfig.ogImage),
      telephone: siteConfig.contact.phone,
      email: siteConfig.contact.email,
      url: siteConfig.url,
      address: {
        "@type": "PostalAddress",
        addressRegion: "Tamil Nadu",
        addressCountry: "IN",
      },
      priceRange: "₹₹",
    },
  };
}

export function getProductSchema(product: Product) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: [absoluteUrl(product.image)],
    description:
      product.description || `Premium handcrafted ${product.name} from ${siteConfig.name}.`,
    sku: product.id,
    brand: {
      "@type": "Brand",
      name: siteConfig.shortName,
    },
    offers: {
      "@type": "Offer",
      url: `${siteConfig.url}/product/${product.slug || getProductSlug(product)}`,
      priceCurrency: siteConfig.currency,
      price: product.price,
      itemCondition: "https://schema.org/NewCondition",
      availability:
        product.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "IN",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: siteConfig.shipping.returnDays,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value:
            product.price >= siteConfig.shipping.freeThreshold
              ? 0
              : siteConfig.pricing.standardShippingFee,
          currency: siteConfig.currency,
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "IN",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 1,
            unitCode: "d",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 5,
            maxValue: 7,
            unitCode: "d",
          },
        },
      },
    },
  };

  const reviewCount = product.reviewsCount || 1;
  const ratingValue = product.rating || 5;

  if (reviewCount > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: ratingValue,
      reviewCount: reviewCount,
    };
    schema.review = {
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: ratingValue,
        bestRating: "5",
      },
      author: {
        "@type": "Person",
        name: "Verified Customer",
      },
    };
  }

  return schema;
}

/**
 * CollectionPage + ItemList for a category landing page.
 *
 * ItemList carries the member products as bare URLs rather than nested Product objects.
 * Repeating the full Product node here would duplicate every field already published by
 * getProductSchema() on the product page itself, and give two markup sources that can
 * disagree about price or availability the moment one is updated. A URL reference points a
 * crawler at the single authoritative copy instead.
 *
 * No aggregateRating anywhere in this tree, for the reason documented in getProductSchema.
 */
export function getCollectionPageSchema(input: {
  name: string;
  description: string;
  url: string;
  productUrls: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: input.url,
    isPartOf: {
      "@type": "WebSite",
      name: siteConfig.name,
      url: siteConfig.url,
    },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: input.productUrls.length,
      itemListElement: input.productUrls.map((url, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url,
      })),
    },
  };
}

export function getFaqSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function getArticleSchema(article: {
  title: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.image ? absoluteUrl(article.image) : undefined,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: siteConfig.name,
      url: siteConfig.url,
    },
    mainEntityOfPage: article.url,
  };
}
